import { FileNode } from '../hooks/useProcessedFiles'

/**
 * Sorts file nodes for display in the file picker.
 *
 * Sorting rules:
 * - Root level (level 0): Matter folder first, Unassigned folder last
 * - All other levels: Folders before files, then alphabetically by displayName
 */
export const sortFileNodes = (a: FileNode, b: FileNode, level: number): number => {
  // Root level: fixed order (Matter first, Unassigned last)
  if (level === 0) {
    if (a.isVirtualFolder) return 1 // Unassigned goes last
    if (b.isVirtualFolder) return -1
    return 0 // Keep original order for other root items
  }

  // Other levels: folders first, then alphabetically
  if (a.isFolder !== b.isFolder) {
    return a.isFolder ? -1 : 1
  }
  return a.displayName.localeCompare(b.displayName)
}

/**
 * Filters nodes to get items that belong in the Unassigned folder.
 * Returns root-level items that are NOT matter folders.
 *
 * @param nodes - All file nodes
 * @param driveRootPath - The root path (e.g., "users/{uid}")
 * @param allMatterIds - Set of all matter IDs to exclude
 */
export const filterUnassignedItems = (nodes: Record<string, FileNode>, driveRootPath: string, allMatterIds: Set<string>): FileNode[] => {
  return Object.values(nodes).filter((node) => {
    // Only include root-level items
    if (node.parentId !== driveRootPath) return false
    // Exclude ALL matter folders by checking if the folder name (last path segment of id) is a matter ID
    // This handles both GCS nodes (where name = folderId) and created nodes (where name = displayName)
    const folderName = node.id.split('/').pop() ?? ''
    if (allMatterIds.has(folderName)) return false
    return true
  })
}

/**
 * Creates a FileNode for the virtual Unassigned folder.
 */
export const createUnassignedFolder = (driveRootPath: string): FileNode => ({
  id: 'unassigned_folder',
  name: 'unassigned_folder',
  displayName: 'Unassigned',
  parentId: driveRootPath,
  isFolder: true,
  isExpanded: false,
  isLoading: false,
  isVirtualFolder: true,
})

/**
 * Creates a FileNode for a matter folder.
 */
export const createMatterFolder = (
  matterFolderPath: string,
  matterDisplayName: string,
  driveRootPath: string,
  existingNode?: Partial<FileNode>,
  isLoading?: boolean
): FileNode => ({
  id: matterFolderPath,
  name: matterDisplayName,
  displayName: matterDisplayName,
  parentId: driveRootPath,
  isFolder: true,
  isExpanded: existingNode?.isExpanded ?? false,
  isLoading: existingNode?.isLoading ?? isLoading ?? false,
})
